import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Health } from '@capgo/capacitor-health'
import { AuthContext } from '../App'
import { getHealthEvents, putHealthEvents } from '../api/user/profile/Profile'

const READ_DATA_TYPES = ['calories', 'steps', 'distance'] as const
const SYNC_DEBOUNCE_MS = 2 * 60 * 1000
const SYNC_REFETCH_MS = 2 * 60 * 1000
const LAST_SYNCED_STORAGE_KEY = 'lvbl:healthkit:lastSyncedAt'

// Module-level so all hook instances share dedupe state. Without this, mounting
// the hook in both UserWrapper and the Activity tab would race two parallel syncs.
let globalLastSyncMs = 0
let globalSyncInFlight = false
export const HEALTHKIT_SYNC_EVENT = 'lvbl:healthkit-sync'
export const HEALTHKIT_SYNCED_EVENT = 'lvbl:healthkit-synced'

type HealthSample = {
  value?: number
}

const sumHealthSamples = (samples: unknown) => {
  if (!Array.isArray(samples)) return 0
  return samples.reduce((total: number, sample: HealthSample) => total + (sample?.value || 0), 0)
}

/**
 * Request health data authorization.
 * Works on both iOS (HealthKit) and Android (Health Connect) via @capgo/capacitor-health.
 */
export const requestHealthKitAuthorization = async (): Promise<boolean> => {
  try {
    const available = await Health.isAvailable()
    if (!available.available) {
      console.warn('[Health] Health data not available on this device:', available.reason)
      return false
    }

    const result = await Health.requestAuthorization({
      read: [...READ_DATA_TYPES],
      write: [],
    })
    return result.readAuthorized.length > 0
  } catch (error) {
    console.error('[Health] Authorization failed:', error)
    return false
  }
}

/**
 * Check if health data is available on this device (iOS or Android native only).
 */
export const isHealthAvailable = (): boolean => {
  return Capacitor.isNativePlatform()
}

/**
 * Fire-and-forget hook to dispatch a sync from anywhere (e.g. the push listener
 * in App.tsx) without needing access to the hook state.
 */
export const triggerHealthKitSync = () => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(HEALTHKIT_SYNC_EVENT))
}

type SyncStatus = 'idle' | 'syncing' | 'success' | 'error'

const readPersistedLastSyncedAt = (): number | null => {
  try {
    const raw = window.localStorage.getItem(LAST_SYNCED_STORAGE_KEY)
    if (!raw) return null
    const parsed = parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : null
  } catch {
    return null
  }
}

const persistLastSyncedAt = (value: number) => {
  try {
    window.localStorage.setItem(LAST_SYNCED_STORAGE_KEY, String(value))
  } catch {
    /* ignore */
  }
}

export const useHealthKitSync = () => {
  const { user } = useContext(AuthContext)
  const queryClient = useQueryClient()
  const lastSyncRef = useRef<number>(0) // local tail of the shared globalLastSyncMs

  const isNative = Capacitor.isNativePlatform()
  const isConnected = user?.member_profile?.healthkit_connected === true

  const [status, setStatus] = useState<SyncStatus>('idle')
  const [lastError, setLastError] = useState<string | null>(null)
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(() =>
    typeof window === 'undefined' ? null : readPersistedLastSyncedAt()
  )

  const missingActivityEvents = useQuery(
    ['missingActivityEvents'],
    () => getHealthEvents({ has_healthkit_data: false, page_size: 50 }),
    {
      enabled: isNative && isConnected,
      refetchOnWindowFocus: false,
      refetchInterval: SYNC_REFETCH_MS,
    }
  )
  const missingActivityEventsData = missingActivityEvents?.data?.data
  const refetchMissingActivityEvents = missingActivityEvents.refetch

  const { mutateAsync: putActivityDataAsync } = useMutation(['putActivityData'], putHealthEvents)

  const performSync = useCallback(
    async (options: { force?: boolean } = {}) => {
      if (!isNative || !isConnected) return
      const { force = false } = options
      const now = Date.now()
      if (!force && now - globalLastSyncMs < SYNC_DEBOUNCE_MS) return
      if (globalSyncInFlight) return
      globalSyncInFlight = true
      globalLastSyncMs = now
      lastSyncRef.current = now

      try {

      // Make sure we have the freshest list of missing events when the user
      // taps "Sync now" or when a silent push arrives.
      let events = missingActivityEventsData
      if (force || !events) {
        const refreshed = await refetchMissingActivityEvents()
        events = refreshed?.data?.data ?? events
      }
      if (!events?.length) {
        setStatus('success')
        const ts = Date.now()
        setLastSyncedAt(ts)
        persistLastSyncedAt(ts)
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent(HEALTHKIT_SYNCED_EVENT, { detail: { at: ts } })
          )
        }
        return
      }

      setStatus('syncing')
      setLastError(null)
      let uploadedAnyEvent = false
      let lastErrorMessage: string | null = null
      const currentTime = new Date()

      for (const event of events) {
        try {
          const startDate = new Date(event.start_time)
          const endDate = new Date(event.end_time)

          if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
            continue
          }
          if (endDate > currentTime) {
            continue
          }

          const startDateISO = startDate.toISOString()
          const endDateISO = endDate.toISOString()

          // Read all three metrics. We must distinguish "read returned 0 samples"
          // from "read failed" — a failure that we silently swallow as 0 would PUT
          // a zero payload, which the backend treats as "synced". That would knock
          // the booking out of the retry loop and the user out of the silent-push
          // queue, freezing them in "synced, no activity" forever.
          const readOrError = async (dataType: 'calories' | 'steps' | 'distance') => {
            try {
              const res = await Health.readSamples({
                dataType,
                startDate: startDateISO,
                endDate: endDateISO,
                limit: 0,
              })
              return { samples: res?.samples, error: null as string | null }
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err)
              return { samples: undefined, error: msg }
            }
          }
          const [caloriesRead, stepsRead, distanceRead] = await Promise.all([
            readOrError('calories'),
            readOrError('steps'),
            readOrError('distance'),
          ])
          const readError =
            caloriesRead.error || stepsRead.error || distanceRead.error
          if (readError) {
            lastErrorMessage = readError
            console.error(`[Health] Read failed for event ${event.id}: ${readError}`)
            // Skip PUT so synced_at stays null and the backend keeps poking us.
            continue
          }

          const calories_value = sumHealthSamples(caloriesRead.samples)
          const steps_value = sumHealthSamples(stepsRead.samples)
          const distance_value = sumHealthSamples(distanceRead.samples)

          // PUT even when all values are 0 — the backend serializer stamps
          // synced_at without writing healthkit_data for zero-only payloads,
          // so the UI gets "Synced X ago" and the retry loop keeps trying
          // until either real data lands or the attempt cap is hit.
          await putActivityDataAsync({
            data: {
              calories: calories_value,
              steps: steps_value,
              distance: distance_value,
            },
            id: event.id,
          })
          if (calories_value > 0 || steps_value > 0 || distance_value > 0) {
            uploadedAnyEvent = true
          }
        } catch (eventError) {
          lastErrorMessage =
            eventError instanceof Error ? eventError.message : String(eventError)
          console.error(`[Health] Failed to process event ${event.id}:`, eventError)
        }
      }

      if (uploadedAnyEvent) {
        queryClient.invalidateQueries(['auth'])
        queryClient.invalidateQueries(['missingActivityEvents'])
        queryClient.invalidateQueries(['getHealthStats'])
        queryClient.invalidateQueries(['getHealthEvents'])
        queryClient.invalidateQueries(['upcomingEvents'])
        queryClient.invalidateQueries(['pastEvents'])
      }

      // Any per-event error (HealthKit read failure, network failure, etc.)
      // counts as an unsuccessful sync — we don't want to bury those by setting
      // status='success' just because some other event got through. We still
      // bump lastSyncedAt locally because the UI's "Synced X ago" tracks
      // attempts, not just clean wins.
      if (lastErrorMessage) {
        setStatus('error')
        setLastError(lastErrorMessage)
      } else {
        setStatus('success')
        setLastError(null)
      }
      const ts = Date.now()
      setLastSyncedAt(ts)
      persistLastSyncedAt(ts)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent(HEALTHKIT_SYNCED_EVENT, { detail: { at: ts } })
        )
      }
      } finally {
        globalSyncInFlight = false
      }
    },
    [
      isNative,
      isConnected,
      missingActivityEventsData,
      putActivityDataAsync,
      queryClient,
      refetchMissingActivityEvents,
    ]
  )

  // Auto-sync on data change (initial mount + react-query refetch interval).
  useEffect(() => {
    performSync()
  }, [performSync])

  // Reset debounce + refetch when the app foregrounds.
  useEffect(() => {
    if (!isNative || !isConnected) return
    const listener = App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        lastSyncRef.current = 0
        refetchMissingActivityEvents()
      }
    })
    return () => {
      listener.then((l) => l.remove())
    }
  }, [isNative, isConnected, refetchMissingActivityEvents])

  // Listen for silent-push wake-ups dispatched from App.tsx push handler.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = () => {
      lastSyncRef.current = 0
      performSync({ force: true })
    }
    window.addEventListener(HEALTHKIT_SYNC_EVENT, handler)
    return () => window.removeEventListener(HEALTHKIT_SYNC_EVENT, handler)
  }, [performSync])

  // Cross-instance state sync: when *any* useHealthKitSync instance finishes a sync,
  // update lastSyncedAt in this instance too (the hook is mounted in UserWrapper and
  // in the Activity tab separately and they need to agree on the timestamp).
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = (e: Event) => {
      const at = (e as CustomEvent<{ at?: number }>).detail?.at
      if (typeof at === 'number') {
        setLastSyncedAt(at)
        setStatus('success')
      }
    }
    window.addEventListener(HEALTHKIT_SYNCED_EVENT, handler)
    return () => window.removeEventListener(HEALTHKIT_SYNCED_EVENT, handler)
  }, [])

  const syncNow = useCallback(() => performSync({ force: true }), [performSync])

  return {
    syncHealthData: performSync,
    syncNow,
    missingActivityEvents,
    status,
    lastError,
    lastSyncedAt,
    isSupported: isNative,
    isConnected,
  }
}
